import type { StudioJobType } from '../studioTypes'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

// Разбор отказов ЧЕТЫРЁХ стартов в одном месте: таблица §1.6 контракта.
// Потребителям (C-G) достаётся размеченный результат — они ничего не парсят.
export type StartFailure =
  | { kind: 'not_configured'; message: string; detail?: string }
  // `courseId` — черновик, УЖЕ созданный упавшим стартом (§1.6: 502 и 503 без
  // кода возвращают `course_id`). Повтор обязан прислать его назад, иначе бэк
  // заводит новый пустой черновик на каждый клик. У `not_configured` и `500`
  // его нет по контракту — поле опциональное, выдумывать нечего.
  | { kind: 'unavailable'; message: string; detail?: string; courseId?: number }
  | { kind: 'rejected'; message: string; courseId?: number }
  | {
      kind: 'course_busy'
      jobId: number
      jobType: StudioJobType
      /** Статус занявшего прогона. `awaiting_author` — курс занят ВАМИ же, и
       *  делать надо не «ждать», а «ответить». Пустая строка — старый ответ
       *  бэка без этого ключа. */
      status: string
      /** id открытого вопроса занявшего прогона — разбирается из `gate_id`,
       *  но решение «ответить или ждать» принимается по `status` выше, не по
       *  этому полю (M-1, финальное ревью): нет ветки, которая делает что-то
       *  ТОЛЬКО когда гейт есть, а `status` при этом другой. Оставлено
       *  распарсенным, а не выброшенным — тому, кто однажды захочет вести
       *  сразу на конкретный вопрос (а не просто открыть панель прогона),
       *  не придётся заново лезть в контракт 409. */
      gateId: number | null
      message: string
    }
  /** Прогон уже продолжен. Второй кнопки не рисовать: перецепить поллинг на
   *  `jobId` продолжения. Тело этого 409 несёт `job_id` без `job_type`. */
  | { kind: 'already_resumed'; jobId: number; message: string }
  | { kind: 'no_credits'; required?: string; balance?: string; message: string }
  | { kind: 'business'; code: string; message: string }
  | { kind: 'validation'; message: string }
  | { kind: 'forbidden'; message: string; code?: string }
  | { kind: 'not_found'; message: string; code?: string }
  | { kind: 'rate_limited'; message: string }
  | { kind: 'unknown'; message: string }

interface Detail {
  code?: string
  message?: string
  course_id?: number
  job_id?: number
  job_type?: StudioJobType
  status?: string
  gate_id?: number | null
  required?: string
  balance?: string
}

const readDetail = (error: unknown): { status?: number; detail?: unknown } => {
  const e = error as { status?: number; data?: { detail?: unknown } } | undefined
  return { status: e?.status, detail: e?.data?.detail }
}

const asObject = (detail: unknown): Detail =>
  detail && typeof detail === 'object' && !Array.isArray(detail) ? (detail as Detail) : {}

const text = (d: Detail, fallback: string): string => d.message ?? fallback

// Тексты на языке интерфейса — в момент разбора.
const msg = () => messagesNow(messages)

// 503 — единственная пара веток, где `message` от бэка НЕ показываем: это
// либо технический текст n8n ("n8n_not_configured"), либо апстрим-таймаут —
// оба нечитаемы автору. Наш текст ставится безусловно, серверная строка
// уходит в `detail` для диагностики (Sentry/логи), а не на экран.
const check503 = (d: Detail): StartFailure =>
  d.code
    ? {
        kind: 'not_configured',
        message: msg().notConfigured,
        detail: d.message,
      }
    : {
        kind: 'unavailable',
        message: msg().unavailable,
        detail: d.message,
        courseId: d.course_id,
      }

const tryAlreadyResumed = (d: Detail): StartFailure | undefined =>
  d.code === 'already_resumed' && typeof d.job_id === 'number'
    ? { kind: 'already_resumed', jobId: d.job_id, message: text(d, msg().alreadyResumed) }
    : undefined

const tryCheck409 = (d: Detail): StartFailure | undefined =>
  typeof d.job_id === 'number' && d.job_type
    ? {
        kind: 'course_busy',
        jobId: d.job_id,
        jobType: d.job_type,
        status: d.status ?? '',
        gateId: d.gate_id ?? null,
        message: text(d, msg().courseBusy),
      }
    : undefined

const tryCheck400 = (d: Detail): StartFailure | undefined =>
  d.code === 'not_enough_ai_credits'
    ? {
        kind: 'no_credits',
        required: d.required,
        balance: d.balance,
        message: text(d, msg().noCredits),
      }
    : undefined

const check422 = (detail: unknown, d: Detail): StartFailure => {
  if (Array.isArray(detail)) {
    return { kind: 'validation', message: msg().validation }
  }
  return { kind: 'business', code: d.code ?? '', message: text(d, msg().businessFallback) }
}

export const parseStartError = (error: unknown): StartFailure => {
  const { status, detail } = readDetail(error)
  const d = asObject(detail)
  const m = messagesNow(messages)

  if (status === 503) return check503(d)
  if (status === 502)
    return { kind: 'rejected', message: text(d, m.rejected), courseId: d.course_id }
  if (status === 409) {
    const resumed = tryAlreadyResumed(d)
    if (resumed) return resumed
    const result = tryCheck409(d)
    if (result) return result
  }
  if (status === 400) {
    const result = tryCheck400(d)
    if (result) return result
  }
  if (status === 422) return check422(detail, d)
  if (status === 403) return { kind: 'forbidden', message: text(d, m.forbidden), code: d.code }
  if (status === 404) return { kind: 'not_found', message: text(d, m.notFound), code: d.code }
  if (status === 429) return { kind: 'rate_limited', message: text(d, m.rateLimited) }
  return { kind: 'unknown', message: text(d, m.startFailed) }
}

/**
 * Кнопку «повторить» рисовать ТОЛЬКО здесь: ретраебельность объявляет сервер,
 * и `retry: true` бывает ровно у одного случая — 503 «сервис недоступен».
 * Слепой повтор на этом контракте плодит лишние курсы и лишние списания.
 */
export const canRetry = (failure: StartFailure): boolean => failure.kind === 'unavailable'

/**
 * Черновик, созданный упавшим стартом. Повторный старт обязан прислать его
 * назад (§4.7, чек-лист п. 20) — иначе каждый повтор заводит новый пустой курс.
 * Единственное место, знающее, у КАКИХ отказов это поле бывает.
 */
export const failureCourseId = (failure: StartFailure | null): number | undefined =>
  failure && (failure.kind === 'rejected' || failure.kind === 'unavailable')
    ? failure.courseId
    : undefined

/**
 * Генерация не настроена на окружении — свойство ОКРУЖЕНИЯ, а не неудача
 * клика (§1.6: «спрятать/погасить кнопку»). Повторные клики отвечают тем же
 * `503` и плодят пустые черновики.
 */
export const isStartBlocked = (failure: StartFailure | null): boolean =>
  failure?.kind === 'not_configured'
